import axios from "axios";
import apiClient from "@/lib/axios";
import { config } from "@/lib/config";
import {
  User,
  LoginCredentials,
  RegisterData,
  AuthResponse,
} from "@/services/types/auth";

export const authApi = {
  // Login user (tries different endpoints based on user type)
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    console.log("Attempting login with credentials:", {
      email: credentials.email,
      password: "***",
    });

    // Create a separate axios instance for login attempts to avoid interceptor interference
    const loginClient = axios.create({
      baseURL: config.api.baseUrl,
      timeout: 30000, // Increased to 30 seconds for OTP operations
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Try customer login first, then agent, then admin
    try {
      console.log("Trying customer login...");
      const response = await loginClient.post(
        "/api/auth/customer/login",
        credentials
      );
      console.log("Customer login successful:", response.data);
      // Backend returns { success, message, user, token }, frontend expects { user, token }
      return {
        user: response.data.user,
        token: response.data.token,
      };
    } catch (customerError: any) {
      console.log(
        "Customer login failed:",
        customerError.response?.status,
        customerError.message
      );
      try {
        console.log("Trying agent login...");
        const response = await loginClient.post(
          "/api/auth/agent/login",
          credentials
        );
        console.log("Agent login successful:", response.data);
        // Backend returns { success, message, user, token }, frontend expects { user, token }
        return {
          user: response.data.user,
          token: response.data.token,
        };
      } catch (agentError: any) {
        console.log(
          "Agent login failed:",
          agentError.response?.status,
          agentError.message
        );
        try {
          console.log("Trying admin login...");
          const response = await loginClient.post(
            "/api/auth/admin/login",
            credentials
          );
          console.log("Admin login successful:", response.data);
          // Backend returns { success, message, user, token }, frontend expects { user, token }
          return {
            user: response.data.user,
            token: response.data.token,
          };
        } catch (adminError: any) {
          console.log(
            "Admin login failed:",
            adminError.response?.status,
            adminError.message
          );
          // If all three fail, throw the last error
          throw adminError;
        }
      }
    }
  },

  // Register customer (only customers can register)
  register: async (userData: RegisterData): Promise<AuthResponse> => {
    const response = await apiClient.post(
      "/api/auth/customer/register",
      userData
    );
    return response.data;
  },

  // V2 Progressive Registration Endpoints
  updateRegistrationPreferences: async (preferencesData: any): Promise<any> => {
    const response = await apiClient.patch(
      "/api/auth/customer/registration/preferences",
      preferencesData
    );
    return response.data;
  },

  updateRegistrationDetails: async (detailsData: any): Promise<any> => {
    const response = await apiClient.patch(
      "/api/auth/customer/registration/details",
      detailsData
    );
    return response.data;
  },

  // Logout user (client-side only for now)
  logout: async (): Promise<void> => {
    // For now, just clear the token on client side
    // Backend logout can be implemented later if needed
    localStorage.removeItem("token");
  },

  // Get current user profile
  getCurrentUser: async (): Promise<User> => {
    // This will be handled by the auth slice based on stored user type
    const token = localStorage.getItem("token");
    const userType = localStorage.getItem("userType");

    if (!token || !userType) {
      throw new Error("No authentication found");
    }

    // Call the appropriate profile endpoint based on user type
    let response;
    if (userType === "admin") {
      response = await apiClient.get("/api/auth/admin/profile");
    } else if (userType === "agent") {
      response = await apiClient.get("/api/auth/agent/profile");
    } else {
      response = await apiClient.get("/api/auth/customer/profile");
    }

    return response.data.data;
  },

  // Update user profile
  updateProfile: async (userData: Partial<User>): Promise<User> => {
    const userType = localStorage.getItem("userType");

    let response;
    if (userType === "admin") {
      // Admin profile updates can be added later
      throw new Error("Admin profile updates not implemented yet");
    } else if (userType === "agent") {
      response = await apiClient.put("/api/auth/agent/profile", userData);
    } else {
      response = await apiClient.put("/api/auth/customer/profile", userData);
    }

    return response.data.data;
  },

  // Forgot password
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await apiClient.post("/api/auth/forgot-password", {
      email,
    });
    return response.data;
  },

  // Reset password
  resetPassword: async (
    token: string,
    password: string
  ): Promise<{ message: string }> => {
    const response = await apiClient.post("/api/auth/reset-password", {
      token,
      password,
    });
    return response.data;
  },

  // Verify email
  verifyEmail: async (token: string): Promise<{ message: string }> => {
    const response = await apiClient.post("/api/auth/verify-email", { token });
    return response.data;
  },

  // Resend verification email
  resendVerification: async (): Promise<{ message: string }> => {
    const response = await apiClient.post("/api/auth/resend-verification");
    return response.data;
  },

  // Change password
  changePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<{ message: string }> => {
    const response = await apiClient.put("/api/auth/change-password", {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  // Delete account
  deleteAccount: async (password: string): Promise<{ message: string }> => {
    const response = await apiClient.delete("/api/auth/account", {
      data: { password },
    });
    return response.data;
  },
};
